import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import Layout from "components/Layout";
import { Nav, Tab } from "react-bootstrap";
import {
	RxBell,
	RxCheckCircled,
	RxCrossCircled,
	RxExclamationTriangle,
	RxCalendar
} from "react-icons/rx";
import { FaRegComment } from "react-icons/fa";
import { RiGroupLine } from "react-icons/ri";
import Loading from "components/Loading";
import { alertNotificationsService as notificationsService, notificationsSocketService } from "services";
import { getNotificationNavigationRoute, navigateToNotificationRoute } from "services/notificationsSocketService";
import { authService } from "services";
import moment from "moment";
import "moment/locale/es";

class Notifications extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			notifications: [],
			selectedTab: "all",
			unreadCount: 0,
		};
		moment;
	}

	async componentDidMount() {
		await this.loadNotifications();

		// El WebSocket ya está conectado y suscrito al topic desde App.js
		// Solo registramos el callback para recibir notificaciones
		this.notificationCallback = (notification) => {
			// Cuando llega una nueva notificación, agregarla a la lista y actualizar contadores
			this.setState((prevState) => {
				const updatedNotifications = [notification, ...prevState.notifications];
				const unreadCount = updatedNotifications.filter((n) => !n.read).length;
				return {
					notifications: updatedNotifications,
					unreadCount,
				};
			});
		};

		notificationsSocketService.registerCallback(this.notificationCallback);
	}

	componentWillUnmount() {
		// Desregistrar el callback cuando el componente se desmonte
		if (this.notificationCallback) {
			notificationsSocketService.unregisterCallback(this.notificationCallback);
		}
	}

	loadNotifications = async () => {
		this.setState({ loading: true });
		try {
			const thirtyDaysAgo = moment().subtract(30, "days").format("YYYY-MM-DD");
			const response = await notificationsService.getAll(thirtyDaysAgo);

			if (response.ok && response.data) {
				const unreadCount = response.data.filter((n) => !n.read).length;
				this.setState({
					notifications: response.data,
					unreadCount,
					loading: false,
				});
			} else {
				// Si no hay datos, inicializar con arrays vacíos
				this.setState({
					notifications: [],
					unreadCount: 0,
					loading: false,
				});
			}
		} catch (error) {
			console.error("Error al cargar notificaciones:", error);
			this.setState({
				notifications: [],
				unreadCount: 0,
				loading: false
			});
		}
	};

	handleTabChange = (tab) => {
		this.setState({ selectedTab: tab });
	};

	handleMarkAsRead = async (notificationId) => {
		try {
			const response = await notificationsService.markAsRead(notificationId, true);
			if (response.ok) {
				// Actualizar el estado local
				this.setState((prevState) => {
					const updatedNotifications = prevState.notifications.map((n) =>
						n.id === notificationId ? { ...n, read: true } : n
					);
					const unreadCount = updatedNotifications.filter((n) => !n.read).length;
					return {
						notifications: updatedNotifications,
						unreadCount,
					};
				});

				// Notificar al sidebar que se marcó como leída
				notificationsSocketService.onNotificationReadChanged(notificationId, true);
			}
		} catch (error) {
			console.error("Error al marcar notificación como leída:", error);
		}
	};

	handleMarkAllAsRead = async () => {
		try {
			// Obtener todas las notificaciones no leídas
			const unreadNotifications = this.state.notifications
				.filter((n) => !n.read)
				.map((n) => ({ id: n.id, read: true }));

			if (unreadNotifications.length === 0) return;

			const response = await notificationsService.markMultipleAsRead(
				unreadNotifications
			);
			if (response.ok) {
				// Notificar al sidebar que todas se marcaron como leídas
				unreadNotifications.forEach((n) => {
					notificationsSocketService.onNotificationReadChanged(n.id, true);
				});

				// Actualizar el estado local
				this.setState((prevState) => {
					const updatedNotifications = prevState.notifications.map((n) => ({
						...n,
						read: true,
					}));
					return {
						notifications: updatedNotifications,
						unreadCount: 0,
					};
				});
			}
		} catch (error) {
			console.error("Error al marcar todas como leídas:", error);
		}
	};


	getNotificationIcon = (type, notification = null) => {
		const baseIconStyle = {
			width: "40px",
			height: "40px",
			borderRadius: "50%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			fontSize: "20px",
			flexShrink: 0,
		};

		// Mapeo de tipos de notificaciones a iconos y colores según el mockup
		const notificationTypes = {
			// Cambios en planes asignados
			AlertPlanChangedEmailEvent: {
				icon: <RxCalendar />,
				color: "#8B5CF6", // Púrpura
			},
			// Cambios en grupos
			AlertGroupChangedEmailEvent: {
				icon: <RiGroupLine />,
				color: "#9CA3AF", // Gris oscuro (como en el mockup)
			},
			// Solicitud de intercambio de turnos
			PlanAssignmentExchangeRequestEmailEvent: {
				icon: <RxExclamationTriangle />,
				color: "#F59E0B", // Amarillo/Naranja
			},
			// Aceptación de intercambio de turnos
			PlanAssignmentExchangeAcceptEmailEvent: {
				icon: <RxCheckCircled />,
				color: "#10B981", // Verde
			},
			// Cambio de estado en intercambio de turnos (aprobado/rechazado)
			PlanAssignmentExchangeStatusChangeEmailEvent: {
				// Podría ser aprobado o rechazado, por defecto check
				icon: <RxCheckCircled />,
				color: "#10B981", // Verde
			},
			// Registro de tiempo no realizado
			TimeRegistrationNotDoneEmailEvent: {
				icon: <RxExclamationTriangle />,
				color: "#EC4899", // Rosa (como en el mockup)
			},
			// Recordatorio de registro de tiempo
			TimeRegistrationReminderEmailEvent: {
				icon: <RxExclamationTriangle />,
				color: "#EC4899", // Rosa
			},
			ChatMessageEmailEvent: {
				icon: <FaRegComment />,
				color: "#5B49FE",
			},
			// Solicitud de workflow/vacaciones (para supervisores)
			WorkflowRequestEmailEvent: {
				icon: <RxCalendar />,
				color: "#8B5CF6", // Púrpura
			},
			WorkflowRequestSecondLevelEmailEvent: {
				icon: <RxCalendar />,
				color: "#8B5CF6", // Púrpura
			},
			// Cambio de estado de workflow/vacaciones (para empleados)
			// Puede ser aprobado (CheckCircled) o rechazado (CrossCircled)
			WorkflowStatusChangeEmailEvent: {
				// Por defecto aprobado, pero se puede determinar por el body o subject
				icon: <RxCheckCircled />,
				color: "#8B5CF6", // Púrpura (como vacaciones aprobadas en mockup)
			},
		};

		// Para WorkflowStatusChangeEmailEvent, intentar determinar si es aprobado o rechazado
		let notificationType = notificationTypes[type];
		if (type === "WorkflowStatusChangeEmailEvent" && notification) {
			const body = notification.body || "";
			const subject = notification.subject || "";
			const isRejected =
				body.toLowerCase().includes("rechaz") ||
				body.toLowerCase().includes("deneg") ||
				subject.toLowerCase().includes("rechaz") ||
				subject.toLowerCase().includes("deneg");

			if (isRejected) {
				notificationType = {
					icon: <RxCrossCircled />,
					color: "#EF4444", // Rojo para rechazado
				};
			}
		}

		// Si no existe el tipo, usar por defecto
		if (!notificationType) {
			notificationType = {
				icon: <RxBell />,
				color: "#9CA3AF", // Gris por defecto
			};
		}

		// Convertir color hex a rgba con opacidad para el fondo
		const hexToRgba = (hex, opacity) => {
			const r = parseInt(hex.slice(1, 3), 16);
			const g = parseInt(hex.slice(3, 5), 16);
			const b = parseInt(hex.slice(5, 7), 16);
			return `rgba(${r}, ${g}, ${b}, ${opacity})`;
		};

		return (
			<div
				style={{
					...baseIconStyle,
					backgroundColor: hexToRgba(notificationType.color, 0.1)
				}}
			>
				{React.cloneElement(notificationType.icon, {
					style: { color: notificationType.color, fontSize: "20px" }
				})}
			</div>
		);
	};

	formatTimeAgo = (dateString) => {
		const date = moment.utc(dateString).local();
		return date.fromNow();
	};

	getFilteredNotifications = () => {
		const { notifications, selectedTab } = this.state;
		if (selectedTab === "unread") {
			return notifications.filter((n) => !n.read);
		}
		return notifications;
	};

	render() {
		const { t } = this.props;
		const { loading, selectedTab, unreadCount } = this.state;
		const filteredNotifications = this.getFilteredNotifications();

		if (loading) {
			return (
				<Layout>
					<Loading />
				</Layout>
			);
		}

		return (
			<Layout>
				<div className="notifications-page">
					<div className="heading">
						<div className="heading-left">
							<h1 className="notifications-title">{t("notifications.title")}</h1>
						</div>
					</div>

					<Tab.Container
						activeKey={selectedTab}
						onSelect={this.handleTabChange}
					>
						<div className="notifications-tabs-row">
							<Nav variant="tabs" className="notifications-tabs">
								<Nav.Item>
									<Nav.Link eventKey="all">{t("notifications.tabAll")}</Nav.Link>
								</Nav.Item>
								<Nav.Item>
									<Nav.Link eventKey="unread">{t("notifications.tabUnread")}
										{unreadCount > 0 && (
											<span className="badge badge-unread">
												{unreadCount}
											</span>
										)}
									</Nav.Link>
								</Nav.Item>
							</Nav>
							{unreadCount > 0 && (
								<button
									type="button"
									className="mark-all-read-link"
									onClick={this.handleMarkAllAsRead}
								>
									{t("notifications.markAllRead")}
								</button>
							)}
						</div>

						<Tab.Content>
							<div className="notifications-list">
								{filteredNotifications.length === 0 ? (
									<div className="empty-notifications">
										<RxBell />
										<p>
											{selectedTab === "unread"
												? t("notifications.emptyUnread")
												: t("notifications.empty")}
										</p>
									</div>
								) : (
									filteredNotifications.map((notification) => {
										const navigationRoute = getNotificationNavigationRoute(notification.type);

										const handleNotificationClick = () => {
											// Marcar como leída si no está leída
											if (!notification.read) {
												this.handleMarkAsRead(notification.id);
											}
											// Navegar a la ruta correspondiente
											if (navigationRoute) {
												navigateToNotificationRoute(navigationRoute);
											}
										};

										return (
											<div
												key={notification.id}
												className={`notification-item ${!notification.read ? "unread" : ""
													}`}
												onClick={handleNotificationClick}
											>
												<div className="notification-icon-wrapper">
													{this.getNotificationIcon(notification.type, notification)}
												</div>
												<div className="notification-content">
													<div className="notification-subject">
														{notification.subject}
													</div>
													<div
														className="notification-body"
														dangerouslySetInnerHTML={{
															__html: notification.body,
														}}
													/>
													<div className="notification-time">
														{this.formatTimeAgo(notification.created)}
													</div>
												</div>
												{!notification.read && (
													<div className="notification-unread-indicator"></div>
												)}
											</div>
										);
									})
								)}
							</div>
						</Tab.Content>
					</Tab.Container>
				</div>
			</Layout>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		user: state.authReducer.user,
	};
};

export default connect(mapStateToProps)(withTranslation()(Notifications));

